import type { SaveEntry } from './grid.save'

/**
 * Whether a save entry should still overlay the value from the listener.
 *
 * `pending` and `error` entries always win. A `confirmed` entry only wins
 * while the listener has not delivered its version yet, so it can never mask
 * a newer write from someone else.
 *
 * @param entry Save entry, if any
 * @param storedUpdatedAt `updatedAt` of the cell in the listener store
 */
export function isSaveEntryVisible(
  entry: SaveEntry | undefined,
  storedUpdatedAt: Date | undefined,
): boolean {
  if (!entry) return false
  if (entry.status !== 'confirmed') return true

  return toMs(storedUpdatedAt) < entry.confirmedUpdatedAt
}

/**
 * Latest version of a cell this client knows of, used as the optimistic
 * locking precondition.
 *
 * The listener store can lag behind our own confirmed save. Editing on top of
 * the confirmed content means the client has seen that version.
 *
 * @param storedUpdatedAt `updatedAt` of the cell in the listener store
 * @param entry Previous save entry for the cell, if any
 */
export function getLastSeenUpdatedAt(
  storedUpdatedAt: Date | undefined,
  entry: SaveEntry | undefined,
): Date | undefined {
  if (entry?.status !== 'confirmed') return storedUpdatedAt
  if (toMs(storedUpdatedAt) >= entry.confirmedUpdatedAt) return storedUpdatedAt

  return new Date(entry.confirmedUpdatedAt)
}

function toMs(date: Date | undefined): number {
  const ms = date?.getTime()
  return ms === undefined || Number.isNaN(ms) ? 0 : ms
}
